import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from "@angular/core";
import { LocationViewModel } from "../../../../shared/models/locations/LocationViewModel.model";

@Component({
  selector: "app-location-details-tab",
  templateUrl: "./location-details-tab.component.html",
  styleUrls: ["./location-details-tab.component.css"],
})
export class LocationDetailsTabComponent implements OnInit, OnChanges {
  @Input() location: LocationViewModel;
  @Input() isEditMode: boolean;
  @Input() countries: any[];
  @Input() states: any[];
  @Output() hasChanges = new EventEmitter<boolean>();
  @Output() countryChange = new EventEmitter<void>();
  @Output() fieldChange = new EventEmitter<void>();

  private originalLocation: string;
  displayLocationDialog: boolean = false;

  // Timezone options - using IANA timezone identifiers that work in both C# and Angular
  timezoneOptions: { label: string; value: string }[] = [
    { label: 'Eastern Time (ET)', value: 'America/New_York' },
    { label: 'Central Time (CT)', value: 'America/Chicago' },
    { label: 'Mountain Time (MT)', value: 'America/Denver' },
    { label: 'Mountain Time - Arizona (MST)', value: 'America/Phoenix' },
    { label: 'Pacific Time (PT)', value: 'America/Los_Angeles' },
    { label: 'Alaska Time (AKT)', value: 'America/Anchorage' },
    { label: 'Hawaii Time (HST)', value: 'Pacific/Honolulu' },
    { label: 'Atlantic Time (AT)', value: 'America/Halifax' },
    { label: 'Newfoundland Time (NT)', value: 'America/St_Johns' },
    { label: 'Saskatchewan Time (CST)', value: 'America/Regina' },
    { label: 'Central Time - Mexico (CST)', value: 'America/Mexico_City' },
    { label: 'Mountain Time - Mexico (MST)', value: 'America/Chihuahua' },
    { label: 'Pacific Time - Mexico (PST)', value: 'America/Tijuana' },
  ];

  // Google Maps properties
  mapCenter: google.maps.LatLngLiteral = { lat: 39.8283, lng: -98.5795 }; // Default to center of US
  mapZoom: number = 10;
  markerPosition: google.maps.LatLngLiteral | null = null;
  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    mapTypeControl: true,
    scaleControl: true,
    streetViewControl: true,
    rotateControl: true,
    fullscreenControl: true,
  };
  markerOptions: google.maps.MarkerOptions = {
    draggable: true,
    animation: google.maps.Animation.DROP,
  };

  constructor() {}

  ngOnInit() {
    this.originalLocation = JSON.stringify(this.location);
    this.initializeMapCenter();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.location && !changes.location.firstChange) {
      this.originalLocation = JSON.stringify(this.location);
      this.initializeMapCenter();
    }
  }

  onFieldChange() {
    const currentLocation = JSON.stringify(this.location);
    const hasChanges = this.originalLocation !== currentLocation;
    this.hasChanges.emit(hasChanges);
    this.fieldChange.emit();
  }

  onCountryChange() {
    this.onFieldChange();
    this.countryChange.emit();
  }

  onLocationPinClick() {
    this.displayLocationDialog = true;
  }

  closeLocationDialog() {
    this.displayLocationDialog = false;
  }

  onDialogShow() {
    // Initialize map when dialog opens
    this.initializeMapCenter();
  }

  onDialogHide() {
    // Cleanup if needed
  }

  initializeMapCenter() {
    if (this.location.latitude && this.location.longitude) {
      this.mapCenter = {
        lat: this.location.latitude,
        lng: this.location.longitude,
      };
      this.markerPosition = {
        lat: this.location.latitude,
        lng: this.location.longitude,
      };
      this.mapZoom = 15; // Zoom in when location is set
    } else {
      // Default to center of US if no coordinates
      this.mapCenter = { lat: 39.8283, lng: -98.5795 };
      this.markerPosition = null;
      this.mapZoom = 4;
    }
  }

  onMapClick(event: google.maps.MapMouseEvent) {
    if (event.latLng && this.isEditMode) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      // Update location coordinates
      this.location.latitude = lat;
      this.location.longitude = lng;

      // Update marker position
      this.markerPosition = { lat, lng };

      // Update map center
      this.mapCenter = { lat, lng };

      // Emit change event
      this.onFieldChange();
    }
  }

  onMarkerDragEnd(event: any) {
    if (event && this.isEditMode) {
      let lat: number;
      let lng: number;

      // Try to get position from event first
      if (event.latLng) {
        lat = event.latLng.lat();
        lng = event.latLng.lng();
      } else if (event.position) {
        lat = typeof event.position.lat === 'function' ? event.position.lat() : event.position.lat;
        lng = typeof event.position.lng === 'function' ? event.position.lng() : event.position.lng;
      } else if (this.markerPosition) {
        // Fallback to marker position
        lat = this.markerPosition.lat;
        lng = this.markerPosition.lng;
      } else {
        return;
      }

      // Update location coordinates
      this.location.latitude = lat;
      this.location.longitude = lng;

      // Update marker position to ensure sync
      this.markerPosition = { lat, lng };

      // Emit change event
      this.onFieldChange();
    }
  }
}

